import React, {useState} from 'react';
import {Stack, styled, TextField} from '@mui/material';
import {Body, Button, designSystemToken} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import {SrtItem} from '@contentlab/utils/srtUtils';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {DURATION_MINUTES_SECONDS_FORMAT} from '@/config/timeFormats';
import {formatTime} from '../util';

const Container = styled(Stack)(() => ({
  backgroundColor: 'rgba(237, 230, 255, 0.3)',
  borderRadius: '16px',
  padding: '8px',
  gap: '8px',
}));

const Header = styled(Stack)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 8px 0 4px',
}));

const SrtTextField = styled(TextField)(() => ({
  'backgroundColor': designSystemToken('semantic.bg.secondary'),
  'borderRadius': '8px',
  '& .MuiInputBase-input': {
    fontSize: '18px',
    color: designSystemToken('semantic.fg.primary'),
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      border: `1px solid ${designSystemToken('semantic.bg.neutral-inverse')}`,
    },
    '&:hover fieldset': {
      border: `1px solid ${designSystemToken('semantic.bg.neutral-inverse')}`,
    },
    '&.Mui-focused fieldset': {
      border: `1px solid ${designSystemToken('semantic.bg.neutral-inverse')}`,
    },
  },
}));

const Footer = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '4px',
}));

function SrtRowEditor({
  testID = 'srt-row-editor',
  item,
  editRow,
  isEdited,
  restoreOriginal,
  currentIndex,
  setCurrentIndex,
}: {
  testID?: string;
  item: SrtItem;
  editRow: (newText: string, shouldCloseRow: boolean) => void;
  isEdited: boolean;
  restoreOriginal: () => void;
  currentIndex: string | null;
  setCurrentIndex: (index: string | null) => void;
}) {
  const {uiInteraction} = useUiInteractionEvent();
  const [newText, setNewText] = useState(item.currentText);

  const onSubmit = (shouldCloseRow = true) => {
    uiInteraction({
      item_name: 'captions_row_editor_update_script',
      interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS_ROW_EDITOR,
      interaction_details: newText,
      flow_name: FLOW_NAMES.CONTENT_LAB.CAPTIONS,
    });
    editRow(newText, shouldCloseRow);
  };

  return (
    <Container data-testid={testID}>
      <Header>
        <Body
          size="md"
          color={designSystemToken('semantic.fg.inverse-secondary')}
        >
          {`${formatTime(
            item.startTime,
            DURATION_MINUTES_SECONDS_FORMAT
          )} → ${formatTime(item.endTime, DURATION_MINUTES_SECONDS_FORMAT)}`}
        </Body>
      </Header>
      <SrtTextField
        multiline
        maxRows={4}
        autoFocus
        value={newText}
        onChange={(e) => {
          setNewText(e.target.value);
          editRow(e.target.value, false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit(false);
            if (currentIndex !== null) {
              setCurrentIndex(String(Number.parseInt(currentIndex, 10) + 1));
            }
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onSubmit(true);
          }
        }}
        inputProps={{
          spellCheck: false,
          autoCapitalize: 'off',
          autoCorrect: 'off',
        }}
      />
      <Footer>
        <Button
          appearance="neutral"
          mode="plain"
          size="small"
          disabled={!isEdited}
          sx={{
            marginLeft: 'auto',
          }}
          onClick={() => {
            restoreOriginal();
            if (currentIndex !== null) {
              setCurrentIndex(null);
            }
            uiInteraction({
              item_name: 'captions_row_editor_restore_original',
              interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS_ROW_EDITOR,
              interaction_details: item.currentText,
              flow_name: FLOW_NAMES.CONTENT_LAB.CAPTIONS,
            });
          }}
        >
          {translate('Restore original')}
        </Button>
      </Footer>
    </Container>
  );
}
export default SrtRowEditor;
